import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "fit", "imageClassName", "style"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import omit from '../utils/underscore/omit';
import UIImage from './UIImage';
import memoizeOne from 'react-utils/memoizeOne';
import { callIfPossible } from '../core/Functions';
import { requestIdleCallback, cancelIdleCallback } from '../utils/Timers';
import { jsx as _jsx } from "react/jsx-runtime";
const ImageWrapper = styled.div.withConfig({
  displayName: "UIFittedImage__ImageWrapper",
  componentId: "vhppyo-0"
})(["overflow:hidden;height:100%;width:100%;"]);

/** @internal */

const getStyle = (style, overrides) => overrides ? Object.assign({
  style
}, overrides) : style;
export default class UIFittedImage extends Component {
  constructor(props) {
    super(props);
    this.wrapperRefCallback = ref => {
      this._wrapper = ref;
    };
    this.imgRefCallback = ref => {
      this._img = ref;
    };
    this.handleImageLoad = evt => {
      const {
        onLoad
      } = this.props;
      this.computeOverrides();
      callIfPossible(onLoad, evt);
    };
    this.delayedComputeOverrides = () => {
      cancelIdleCallback(this._timeout); // Ensure only one callback is queued at a time
      this._timeout = requestIdleCallback(this.computeOverrides);
    };
    this.computeOverrides = () => {
      const {
        fit
      } = this.props;
      if ('objectFit' in document.documentElement.style || fit === 'fill') return;
      const wrapperEl = this._wrapper;
      const imgEl = this._img;
      if (!wrapperEl || !imgEl) return;
      const {
        naturalWidth,
        naturalHeight
      } = imgEl;
      if (!naturalWidth || !naturalHeight) return;
      const imgAspectRatio = naturalWidth / naturalHeight;
      const {
        width,
        height
      } = wrapperEl.getBoundingClientRect();
      const wrapperAspectRatio = width / height;
      let overrides = null;

      // Polyfill `object-fit: cover`
      if (fit === 'cover') {
        if (wrapperAspectRatio > imgAspectRatio) {
          const heightOverride = width / imgAspectRatio;
          overrides = {
            maxHeight: 'none',
            height: heightOverride,
            marginTop: -(heightOverride - height) / 2
          };
        } else if (wrapperAspectRatio < imgAspectRatio) {
          const widthOverride = height * imgAspectRatio;
          overrides = {
            maxWidth: 'none',
            width: widthOverride,
            marginLeft: -(widthOverride - width) / 2
          };
        }
      }

      // Polyfill `object-fit: contain`
      if (fit === 'contain') {
        if (wrapperAspectRatio > imgAspectRatio) {
          const widthOverride = height * imgAspectRatio;
          overrides = {
            width: widthOverride,
            marginLeft: (width - widthOverride) / 2
          };
        } else if (wrapperAspectRatio < imgAspectRatio) {
          const heightOverride = width / imgAspectRatio;
          overrides = {
            height: heightOverride,
            marginTop: (height - heightOverride) / 2
          };
        }
      }
      if (overrides !== this.state.overrides) {
        this.setState({
          overrides
        });
      }
    };
    this.state = {
      overrides: null
    };
    this._getStyle = memoizeOne(getStyle);
    this._wrapper = null;
    this._img = null;
    this._timeout = 0;
  }
  componentDidMount() {
    requestAnimationFrame(this.computeOverrides);
    addEventListener('resize', this.delayedComputeOverrides);
  }
  componentDidUpdate(prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.overrides === this.state.overrides) {
      this.computeOverrides();
    }
  }
  componentWillUnmount() {
    removeEventListener('resize', this.delayedComputeOverrides);
  }
  render() {
    const _this$props = this.props,
      {
        className,
        fit,
        imageClassName,
        style
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      overrides
    } = this.state;
    return /*#__PURE__*/_jsx(ImageWrapper, {
      className: className,
      ref: this.wrapperRefCallback,
      children: /*#__PURE__*/_jsx(UIImage, Object.assign({}, rest, {
        className: classNames(imageClassName, 'private-image--fitted', {
          'contain': 'private-image--object-fit-contain',
          'cover': 'private-image--object-fit-cover'
        }[fit]),
        onLoad: this.handleImageLoad,
        ref: this.imgRefCallback,
        responsive: false,
        style: this._getStyle(style, overrides)
      }))
    });
  }
}
UIFittedImage.propTypes = Object.assign({}, omit(UIImage.propTypes, ['objectFit', 'responsive']), {
  fit: PropTypes.oneOf(['fill', 'contain', 'cover']),
  imageClassName: PropTypes.string
});
UIFittedImage.defaultProps = Object.assign({}, UIImage.defaultProps, {
  fit: 'fill'
});
UIFittedImage.displayName = 'UIFittedImage';